import React, { FormEvent, useState } from "react";

import { authApi } from "../api/auth.api";
import { AuthApiError } from "../model/auth.types";
import { ROUTES } from "../../../system/router/routes";
import {
  AuthAlert,
  AuthCard,
  AuthLink,
  AuthLinkRow,
  AuthPrimaryLink,
  AuthSubmitButton,
  AuthTextField,
  EnvelopeIcon,
} from "./auth-kit";
import { AuthLayout } from "./auth-layout";
import * as Styled from "./auth.styled";

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [userNotFound, setUserNotFound] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [resetToken, setResetToken] = useState("");

  const requestPasswordReset = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setApiError("");
    setUserNotFound(false);
    setSubmittedEmail("");
    setResetToken("");

    if (!email.trim()) {
      setApiError("Enter the email address associated with your account.");
      return;
    }

    setLoading(true);

    try {
      const response = await authApi.forgotPassword({ email });
      setSubmittedEmail(response.email);
      setResetToken(response.resetToken || "");
    } catch (error) {
      const authError = error as AuthApiError;
      setApiError(authError.message || "Unable to send reset instructions.");
      setUserNotFound(authError.code === "USER_NOT_FOUND");
    } finally {
      setLoading(false);
    }
  };

  const alert = apiError ? (
    <AuthAlert
      onClose={() => {
        setApiError("");
        setUserNotFound(false);
      }}
    >
      {userNotFound ? (
        <>
          User with this email was not found. For assistance, contact LDA
          support at{" "}
          <Styled.AlertAction href="mailto:ldasupport@legacydataaccess.com">
            ldasupport@legacydataaccess.com
          </Styled.AlertAction>
          , or call <strong>1-866-601-2416</strong> or{" "}
          <strong>678-370-0149</strong>.
        </>
      ) : (
        apiError
      )}
    </AuthAlert>
  ) : undefined;

  if (submittedEmail) {
    return (
      <AuthLayout>
        <AuthCard title="Access instructions sent" icon={<EnvelopeIcon />}>
          <Styled.BodyText role="status">
            Please check your email for further instructions to reset your
            password.
          </Styled.BodyText>

          <Styled.ActionStack>
            <AuthPrimaryLink to={ROUTES.SIGN_IN}>
              Back to Sign in
            </AuthPrimaryLink>

            {resetToken ? (
              <AuthLinkRow>
                <AuthLink
                  to={`${ROUTES.CREATE_PASSWORD}?token=${resetToken}&email=${encodeURIComponent(submittedEmail)}`}
                >
                  Open reset link
                </AuthLink>
              </AuthLinkRow>
            ) : null}
          </Styled.ActionStack>
        </AuthCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout alert={alert}>
      <AuthCard title="Forgot password?">
        <Styled.BodyText>
          We will send you an email with further instructions to reset your
          password.
        </Styled.BodyText>

        <Styled.Form onSubmit={requestPasswordReset} noValidate>
          <AuthTextField
            id="forgot-email"
            label="Email"
            name="email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
          />

          <AuthSubmitButton loading={loading} loadingLabel="Sending...">
            Receive Instructions
          </AuthSubmitButton>

          <AuthLinkRow>
            <AuthLink to={ROUTES.SIGN_IN}>Back to Sign in</AuthLink>
          </AuthLinkRow>
        </Styled.Form>
      </AuthCard>
    </AuthLayout>
  );
};
